import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import { GoogleLogin } from "@react-oauth/google";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { login, loginWithGoogle, forgotPassword } from "../../api/authService";
import { jwtDecode } from "jwt-decode";
import PWAInstallPrompt from "../../components/PWAInstallPrompt";
import useBrowserCheck from "../../hooks/useBrowserCheck";

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false); // Novo estado para o envio de e-mail de redefinição
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [emailEnviado, setEmailEnviado] = useState(null);
  const navigate = useNavigate();

  const isAllowedBrowser = useBrowserCheck();

  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  const [loginAttempts, setLoginAttempts] = useState(
    parseInt(localStorage.getItem("loginAttempts")) || 0
  );
  

  const handleUsernameChange = (e) => {
    const value = e.target.value;
    // Verifica se o valor é composto apenas por números e tem exatamente 11 dígitos
    if (/^\d+$/.test(value) && value.replace(/\D/g, "").length === 11) {
      const formattedValue = value
        .replace(/\D/g, "") // Remove todos os caracteres não numéricos
        .replace(/(\d{3})(\d)/, "$1.$2") // Adiciona o primeiro ponto
        .replace(/(\d{3})(\d)/, "$1.$2") // Adiciona o segundo ponto
        .replace(/(\d{3})(\d{2})$/, "$1-$2"); // Adiciona o traço
      setUsername(formattedValue);
    } else {
      // Para outros casos, trata como texto normal (nome ou email)
      setUsername(value);
    }
  };

  const cleanCPF = (cpf) => cpf.replace(/\D/g, "");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const cleanedUsername = /^\d{3}\.\d{3}\.\d{3}-\d{2}$/.test(username)
        ? cleanCPF(username)
        : username;
      const { token, role } = await login(cleanedUsername, password);
      localStorage.setItem("token", token);
      localStorage.removeItem("loginAttempts");
      setLoginAttempts(0);
      localStorage.setItem("role", role);
      localStorage.setItem("loggedIn", "true");

      if (role === "ROLE_SECRETARIA") navigate("/solicitacoes/PENDENTE");
      else if (role === "ROLE_COMUM") navigate("/home");
      else if (role === "ROLE_PSF") navigate("/home_psf");
      else setError("Role desconhecida.");
    } catch {
      setError("Usuário ou senha inválidos. Tente novamente.");
      setLoginAttempts(prev => {
        const newAttempts = prev + 1;
        localStorage.setItem("loginAttempts", newAttempts);
        return newAttempts;
      });
      
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLoginSuccess = async (response) => {
    const googleToken = response.credential;
    try {
      const { token, role } = await loginWithGoogle(googleToken);
      localStorage.setItem("token", token);
      localStorage.setItem("role", role);
      localStorage.setItem("loggedIn", "true");

      if (role === "ROLE_SECRETARIA") navigate("/solicitacoes/PENDENTE");
      else if (role === "ROLE_COMUM") navigate("/home");
      else if (role === "ROLE_PSF") navigate("/home_psf");
      else setError("Role desconhecida.");
    } catch (error) {
      if (error.response?.status === 500) {
        const decoded = jwtDecode(googleToken);
        const email = decoded.email;
        const name = decoded.name;

        navigate("/cadastrar-usuario", {
          state: { nomeCompleto: name, email, googleUser: true },
        });
      } else {
        setError("Falha na autenticação com Google. Tente novamente.");
      }
    }
  };

  const handleForgotPassword = async () => {
    try {
      setResetLoading(true);
      const data = await forgotPassword(username);
      setModalMessage(`E-mail enviado para ${data.email}`);
      setEmailEnviado(data.email);
    } catch {
      setModalMessage("Erro ao enviar o e-mail. Verifique os dados.");
      setEmailEnviado(null);
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      height="100vh"
      bgcolor="#007bff" // Mantém o fundo azul
      position="relative"
      px={2} // Padding para evitar que encoste nas bordas em telas menores
    >
      <PWAInstallPrompt />
      <Box p={4} maxWidth={400} bgcolor="#fff" borderRadius={4} boxShadow={3}>
      <Box display="flex" justifyContent="center" mb={2}>
      <img
        src="/logo_retangular.png"
        alt="Conecta Vicência"
        width="150"
        style={{
          cursor: "pointer",
          backgroundColor: "white",
          padding: "10px",
          borderRadius: "8px",
        }}
        onClick={() => navigate("/")}
      />
    </Box>
        <Typography variant="h5" align="center" gutterBottom>
          Bem-vindo!
        </Typography>
        <Typography variant="body1" align="center" mb={2}>
          Faça login para acessar sua conta
        </Typography>

        {error && (
          <Typography color="error" align="center" mb={2}>
            {error}
          </Typography>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Usuário, Email ou CPF"
            value={username}
            onChange={handleUsernameChange}
            variant="outlined"
            margin="normal"
            required
          />

          <TextField
            fullWidth
            label="Senha"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            variant="outlined"
            margin="normal"
            required
            InputProps={{
              endAdornment: (
                <IconButton onClick={togglePasswordVisibility} edge="end">
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </IconButton>
              ),
            }}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            disabled={loading}
            sx={{ mt: 2 }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Entrar"
            )}
          </Button>
        </form>

        <Button
      fullWidth
      onClick={() => setModalOpen(true)}
      color="secondary"
      sx={{ mt: 1, fontSize: "0.85rem" }}
    >
          Esqueci a senha
        </Button>

        {loginAttempts >= 3 && (
  <Typography
    align="center"
    mt={2}
    color="error"
    fontSize="0.85rem"
  >
    ⚠️ Você tentou se logar mais de 3 vezes. Se esqueceu sua senha, clique em 
    <Button 
      variant="text" 
      onClick={() => setModalOpen(true)} 
      sx={{ textDecoration: "underline", ml: 1 }}
    >
      Esqueci a senha
    </Button>
    .<br />Caso não consiga, entre em contato com a Secretaria de Saúde.
  </Typography>
)}


        <Typography align="center" mt={1} fontSize="0.85rem">
          Ou entre com:
        </Typography>

        {isAllowedBrowser ? (
          <Box display="flex" justifyContent="center" mt={2}>
            <GoogleLogin
              onSuccess={handleGoogleLoginSuccess}
              onError={() => setError("Falha no login com Google. Tente novamente.")}
            />
          </Box>
        ) : (
          <Typography
            align="center"
            color="error"
            sx={{ mt: 2 }}
          >
            ⚠️ Para fazer login com Google, abra o site em um navegador comum (Chrome, Safari, Firefox).
          </Typography>
        )}

        <Typography align="center" mt={1} fontSize="0.85rem">
          Ainda não tem conta?{" "}
          <Link to="/cadastrar-usuario">Cadastre-se aqui</Link>
        </Typography>
        <Box sx={{ mt: 3, textAlign: "center" }}>
      <Typography variant="caption">
        Desenvolvido por{" "}
        <a
          href="https://github.com/wesleyvicen/"
          target="_blank"
          rel="noopener noreferrer"
          sx={{ fontWeight: "bold" }}
        >
              Wesley Vicente
            </a> <br/>
          </Typography>
          <Typography variant="caption" color="textSecondary">
            &copy; {new Date().getFullYear()} Prefeitura de Vicência-PE.
          </Typography>
        </Box>
      </Box>

      <Dialog open={modalOpen} onClose={() => setModalOpen(false)}>
        <DialogTitle>Recuperação de Senha</DialogTitle>
        <DialogContent>
          <Typography>
            {emailEnviado
              ? `Um e-mail foi enviado para ${emailEnviado}`
              : "Confirme seu usuário para recuperar a senha:"}
          </Typography>
          {!emailEnviado && (
            <TextField
              fullWidth
              margin="normal"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Usuário, Email ou CPF"
            />
          )}
        </DialogContent>
        <DialogActions>
          {!emailEnviado ? (
            <Button
              onClick={handleForgotPassword}
              color="primary"
              disabled={resetLoading}
            >
              {resetLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Enviar E-mail"
              )}
            </Button>
          ) : null}
          <Button onClick={() => setModalOpen(false)} color="secondary">
            Fechar
          </Button>
          <p>{modalMessage}</p>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Login;
